import { Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { formCompletionTime } from '#lib/react-query/queries';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { formatDurationFromSeconds } from '#utils/dates';
import { Base } from '../Base';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useFormWidget } from '../use-form-widget.ts';
import { StatBase } from './StatBase';

const widgetTitle = 'Form Completion Time';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Measures the&nbsp;
        <InfoEmphasis>median time</InfoEmphasis>
        &nbsp;it takes patients to complete&nbsp;
        <InfoEmphasis>required forms</InfoEmphasis>
        &nbsp;after they are assigned. This helps organizations assess patient
        engagement and readiness before appointments, and identify opportunities
        to streamline the pre-visit process.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Median Time to Complete Forms =" indented>
        <InfoList
          component="ol"
          boxProps={{ sx: { pl: 5, mb: 2 } }}
          items={[
            <>
              <Typography variant="body2" fontWeight="bold">
                Filter forms to include only those that:
              </Typography>
              <InfoList
                items={[
                  'Are part of the selected form set (commonly required forms, depending on filters)',
                  'Have both an assignment time and a completion time',
                  'Were completed within the selected date range',
                ]}
              />
            </>,
            <>
              <Typography variant="body2" fontWeight="bold">
                Measure completion time for each form:
              </Typography>
              <InfoList
                items={[
                  'Time is calculated in seconds from when the form was assigned to when it was completed, then converted into hours or days by the front end',
                  'Find the median (middle value) of all completion times (not the average)',
                ]}
              />
            </>,
            <>
              <Typography variant="body2" fontWeight="bold">
                Display the Result:
              </Typography>
              <InfoList
                items={[
                  'If the median is less than 1 day → show in hours (e.g., 3.7 hours)',
                  'If the median is 1 day or more → show in days (e.g., 2.1 days)',
                ]}
              />
            </>,
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Forms within the selected form set',
            'Forms with valid assigned and completed dates',
            'Forms completed within the selected date range (even if assigned earlier)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList items={['Forms never completed', 'Deleted forms']} />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Time is always calculated in seconds and converted for display by the front end',
            'Uses median to reduce skew from extreme delays; do not interpret as an average',
            'Completion date determines inclusion, not assignment date—forms may have been assigned earlier',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function FormCompletionTime() {
  const widget = useFormWidget();

  const { data, isLoading } = useQuery(
    formCompletionTime(widget.getCommonRequestParams())
  );

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      data={{
        id: 'form-completion-time',
        title: widgetTitle,
        value: data?.value ?? null,
        valueFormatted: data?.value
          ? formatDurationFromSeconds(data.value)
          : undefined,
        valueType: 'absolute',
        trend: data?.trend
          ? {
              ...data.trend,
              daysInPeriod: widget.homeLoaderData.totalDaysInRange,
            }
          : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
